import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes, useTheme } from 'styled-components';
import {
  Icon,
  PlainButton,
  ToggleSwitch,
  orange,
  tokens,
  useIsMobile,
} from 'components/v3';
import { FONT_SYSTEM } from 'components/v3/tokens';
import { CHAPTERS, totalSteps } from './chapters';
import { MOBILE_MAX, ORANGE, Z } from './constants';
import { useHelpTipsEnabled } from './tipsSetting';
import { ALL } from './tourModel';

// The Help & tours panel (Implementation List §4, design HelpPanel): the
// full tour, each chapter with its step count, and the Help tips switch.
// Drops down from the Help button on desktop; sits along the bottom edge on
// phones. Esc or a click outside closes it.

const pop = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
`;

const Scrim = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${Z.panel};
`;

const Panel = styled.div`
  position: fixed;
  z-index: ${Z.panel + 1};
  width: 368px;
  overflow-y: auto;
  box-sizing: border-box;
  background: ${({ $dark }) => ($dark ? '#1A1A2E' : '#fff')};
  border: 1px solid
    ${({ $dark }) =>
      $dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)'};
  border-radius: 16px;
  box-shadow: ${({ $dark }) =>
    $dark
      ? '0 18px 48px rgba(0, 0, 0, 0.5)'
      : '0 18px 48px rgba(23, 37, 69, 0.22)'};
  color: ${({ $t }) => $t.text};
  font-family: ${FONT_SYSTEM};
  line-height: 1.5;
  text-align: left;
  animation: ${pop} 160ms ease;

  ${({ $mobile }) =>
    $mobile &&
    css`
      width: auto;
      left: 12px;
      right: 12px;
      bottom: 12px;
      max-height: calc(100vh - 84px);
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 14px 10px 16px;
`;

const HeadIcon = styled.span`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${orange(0.13)};
  border: 1px solid ${orange(0.4)};
  color: ${ORANGE};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeadText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.3;
  color: ${({ $t }) => $t.text};
`;

const Sub = styled.div`
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 1.4;
  color: ${({ $t }) => $t.faint};
`;

const Close = styled(PlainButton)`
  flex: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  color: ${({ $t }) => $t.sub};

  &:hover {
    background: ${({ $t }) => $t.inset};
    color: ${({ $t }) => $t.text};
  }
`;

const Section = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ $dark }) => ($dark ? ORANGE : '#D63A0F')};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 8px 10px;
`;

const Item = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 10px 9px 8px;
  border: 1px solid transparent;
  border-radius: 11px;
  text-align: left;
  transition: background 150ms ease, border-color 150ms ease;

  &:hover,
  &:focus-visible {
    background: ${orange(0.09)};
  }

  ${({ $primary }) =>
    $primary &&
    css`
      margin-bottom: 4px;
      background: ${orange(0.12)};
      border-color: ${orange(0.36)};

      &:hover,
      &:focus-visible {
        background: ${orange(0.18)};
      }
    `}

  @media (max-width: ${MOBILE_MAX}px) {
    min-height: 52px;
  }
`;

const ItemIcon = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $primary, $t }) => ($primary ? ORANGE : $t.inset)};
  color: ${({ $primary, $t }) => ($primary ? '#fff' : $t.sub)};
`;

const ItemText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;

  strong {
    font-size: 14px;
    font-weight: 700;
    color: ${({ $t }) => $t.text};
  }

  span {
    font-size: 12px;
    color: ${({ $t }) => $t.faint};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Count = styled.span`
  flex: none;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ $t }) => $t.faint};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Toggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 16px 16px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${({ $t }) => $t.border};
  background: ${({ $t }) => $t.inset};
`;

const ToggleText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.35;

  strong {
    font-size: 13.5px;
    font-weight: 700;
    color: ${({ $t }) => $t.text};
  }

  span {
    font-size: 12px;
    color: ${({ $t }) => $t.faint};
  }
`;

// Below the Help button, right edges lined up, inside the viewport.
export const panelPosition = (anchor, vw) => {
  if (!anchor) return { top: 72, right: 16, maxHeight: 'calc(100vh - 88px)' };
  const top = Math.round(anchor.bottom + 8);
  return {
    top,
    right: Math.max(12, Math.round(vw - anchor.right)),
    maxHeight: `calc(100vh - ${top + 16}px)`,
  };
};

const HelpPanel = ({ anchor, onClose, onStart }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const dark = !!theme && theme.name === 'dark';
  const isMobile = useIsMobile(MOBILE_MAX + 1);
  const [tipsOn, setTipsOn] = useHelpTipsEnabled();
  const firstRef = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    if (firstRef.current) firstRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected && opener.focus) opener.focus();
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <>
      <Scrim data-testid="help-scrim" onMouseDown={onClose} />
      <Panel
        role="dialog"
        aria-label="Help and tours"
        $t={t}
        $dark={dark}
        $mobile={isMobile}
        style={isMobile ? undefined : panelPosition(anchor, window.innerWidth)}
      >
        <Head>
          <HeadIcon>
            <Icon name="circle-help" size={18} />
          </HeadIcon>
          <HeadText>
            <Title $t={t}>Help &amp; tours</Title>
            <Sub $t={t}>Walk through the form tools one control at a time.</Sub>
          </HeadText>
          <Close
            $t={t}
            data-help="dialog-close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="x" size={17} />
          </Close>
        </Head>
        <Section $dark={dark}>Product tour</Section>
        <List>
          <Item ref={firstRef} $primary onClick={() => onStart(ALL)}>
            <ItemIcon $primary $t={t}>
              <Icon name="flag" size={16} />
            </ItemIcon>
            <ItemText $t={t}>
              <strong>Full tour</strong>
              <span>Forms, building, submissions and exports</span>
            </ItemText>
            <Count $t={t}>{totalSteps()} steps</Count>
          </Item>
          {CHAPTERS.map((chapter) => (
            <Item key={chapter.id} onClick={() => onStart(chapter.id)}>
              <ItemIcon $t={t}>
                <Icon name={chapter.icon} size={16} />
              </ItemIcon>
              <ItemText $t={t}>
                <strong>{chapter.label}</strong>
                <span>{chapter.blurb}</span>
              </ItemText>
              <Count $t={t}>{chapter.steps.length} steps</Count>
            </Item>
          ))}
        </List>
        <Toggle $t={t}>
          <ToggleText $t={t}>
            <strong>Help tips</strong>
            <span>Explain controls when you hover over them</span>
          </ToggleText>
          <ToggleSwitch
            on={tipsOn}
            size="sm"
            label="Help tips"
            onClick={() => setTipsOn(!tipsOn)}
          />
        </Toggle>
      </Panel>
    </>,
    document.body
  );
};

HelpPanel.propTypes = {
  anchor: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
};

HelpPanel.defaultProps = {
  anchor: null,
};

export default HelpPanel;
